export const ErrorCodes = {
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  DOWNLOAD_FAILED: 'DOWNLOAD_FAILED',
  SHARE_FAILED: 'SHARE_FAILED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  FILE_NOT_FOUND: 'FILE_NOT_FOUND',
  INVALID_OPERATION: 'INVALID_OPERATION',
  SHARE_LIMIT_REACHED: 'SHARE_LIMIT_REACHED',
  INVALID_RECIPIENT: 'INVALID_RECIPIENT',
  ALREADY_SHARED: 'ALREADY_SHARED'
};

export function parseApiError(error) {
  try {
    // If error is already an object with error and code properties, return it
    if (error && typeof error === 'object' && 'error' in error && 'code' in error) {
      return {
        code: error.code,
        message: error.error,
        raw: error
      };
    }
    
    // Otherwise try to parse from error message
    const errorData = JSON.parse(error.message.split('API request failed: ')[1]);
    return {
      code: errorData.code,
      message: errorData.error,
      raw: errorData
    };
  } catch (e) {
    // If error is a string, use it as the message
    if (typeof error === 'string') {
      return {
        code: 'UNKNOWN_ERROR',
        message: error,
        raw: error
      };
    }
    // If error is an object with a message property, use that
    if (error && typeof error === 'object' && 'message' in error) {
      return {
        code: 'UNKNOWN_ERROR',
        message: error.message,
        raw: error
      };
    }
    // Fallback
    return {
      code: 'UNKNOWN_ERROR',
      message: 'An unknown error occurred',
      raw: error
    };
  }
}

export function getUploadErrorMessage(error) {
  const parsedError = parseApiError(error);
  
  switch (parsedError.code) {
    case ErrorCodes.UPLOAD_FAILED:
      if (parsedError.message === 'Monthly upload limit reached') {
        return 'You have reached your monthly upload limit. Please try again next month or upgrade your plan.';
      }
      return 'Failed to upload file. Please try again.';
      
    case ErrorCodes.QUOTA_EXCEEDED:
      return 'Storage quota exceeded. Please free up some space or upgrade your plan.';
      
    case ErrorCodes.UNAUTHORIZED:
      return 'Your session has expired. Please log in again.';
      
    default:
      return parsedError.message || 'Error uploading file!';
  }
}

export function getDownloadErrorMessage(error) {
  const parsedError = parseApiError(error);
  
  switch (parsedError.code) {
    case ErrorCodes.DOWNLOAD_FAILED:
      return 'Failed to download file. Please try again.';
      
    case ErrorCodes.FILE_NOT_FOUND:
      return 'The file you are trying to download no longer exists.';
      
    case ErrorCodes.UNAUTHORIZED:
      return 'You do not have permission to download this file.';
      
    default:
      return parsedError.message || 'Error downloading file!';
  }
}

export function getShareErrorMessage(error) {
  const parsedError = parseApiError(error);
  
  switch (parsedError.code) {
    case ErrorCodes.SHARE_FAILED:
      return 'Failed to share file. Please try again.';
      
    case ErrorCodes.FILE_NOT_FOUND:
      return 'The file you are trying to share no longer exists.';
      
    case ErrorCodes.UNAUTHORIZED:
      return 'You do not have permission to share this file.';
      
    case ErrorCodes.INVALID_OPERATION:
      return 'Cannot share this file. It may have been deleted or your permissions may have changed.';
      
    default:
      return parsedError.message || 'Error sharing file!';
  }
}

export function getFileOperationErrorMessage(error) {
  const parsedError = parseApiError(error);
  
  switch (parsedError.code) {
    case ErrorCodes.FILE_NOT_FOUND:
      return 'The file no longer exists or has been deleted.';
    
    case ErrorCodes.UNAUTHORIZED:
      return 'You do not have permission to perform this action.';
    
    case ErrorCodes.SHARE_LIMIT_REACHED:
      return 'You have reached the maximum number of shares for this file.';
    
    case ErrorCodes.INVALID_RECIPIENT:
      return 'One or more recipients are invalid or cannot receive shares.';
    
    case ErrorCodes.ALREADY_SHARED:
      return 'This file is already shared with one or more of the selected contacts.';
    
    default:
      return parsedError.message || 'An error occurred while performing the operation.';
  }
} 